import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { logoutUser } from "../../redux/auth/auth";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import {
  faMoon,
  faSignOut,
  faUserCircle,
} from "@fortawesome/free-solid-svg-icons";
import { faSun } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

function Header() {
  const userDetail = useSelector((state) => state.auth.userDetail);
  const dispatch = useDispatch();
  const location = useLocation();
  const pathname = location.pathname.replace("/", "");
  const [openMenu, setOpenMenu] = useState(false);
  const menuRef = useRef(null);
  const [theme, setTheme] = useState("dark");

  useEffect(() => {
    // Load saved theme from localStorage
    const savedTheme = localStorage.getItem("theme") || "dark";
    setTheme(savedTheme);
    document.documentElement.setAttribute("data-bs-theme", savedTheme);
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === "dark" ? "light" : "dark";
    setTheme(newTheme);
    document.documentElement.setAttribute("data-bs-theme", newTheme);
    localStorage.setItem("theme", newTheme);
  };
  const sections = {
    dashboard: "Dashboard",
    portfolio: "My Portfolio",
    multibagger: "My Multibaggers",
    etf: "My ETFs",
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenu(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const logout = () => dispatch(logoutUser());

  return (
    <>
      <nav
        className="navbar navbar-expand-lg shadow-sm bg-black sticky-top"
        data-bs-theme="dark"
        style={{ borderBottom: "1px solid #1f1f1f" }}
      >
        <div className="container-fluid">
          {/* Mobile Toggler */}
          <button
            className="btn d-lg-none border-0"
            type="button"
            data-bs-toggle="offcanvas"
            data-bs-target="#mobileMenu"
            aria-controls="mobileMenu"
            aria-label="Toggle navigation"
          >
            <Menu size={24} color="white" />
          </button>

          {/* Brand */}
          <Link className="navbar-brand py-0 fw-bold text-white ms-2" to="/">
            <h4 className="fw-bold text-white d-flex align-items-center gap-1 m-0">
              Equitypandit <span className="color-text"> Wealth</span>
            </h4>
          </Link>

          {/* Desktop Navigation */}
          <div
            className="collapse navbar-collapse d-none d-lg-flex justify-content-end"
            id="navbarContent"
          >
            <ul className="navbar-nav ms-auto align-items-center">
              {Object.entries(sections).map(([key, label]) => (
                <li className="nav-item" key={key}>
                  <NavLink
                    className={({ isActive }) =>
                      `nav-link ${isActive ? "active" : "text-white"}`
                    }
                    to={`/${key}`}
                    style={({ isActive }) =>
                      isActive
                        ? {
                          background:
                            "linear-gradient(to right, #a855f7, #3b82f6)",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                        }
                        : {}
                    }
                  >
                    <small>{label}</small>
                  </NavLink>
                </li>
              ))}

              {/* User Avatar Dropdown */}
              <li
                className="nav-item ms-3 d-flex position-relative"
                ref={menuRef}
              >
                <div
                  className="rounded-circle d-flex justify-content-center align-items-center glare px-3 py-2 pill text-body-emphasis fw-bold"
                  style={{ cursor: "pointer" }}
                  onClick={() => setOpenMenu((prev) => !prev)}
                >
                  {userDetail.firstName?.charAt(0).toUpperCase()}
                </div>

                {openMenu && (
                  <div
                    className="dropdown-menu show position-absolute end-0 mt-4 p-3 shadow border rounded bg-dark text-white"
                    style={{ minWidth: "180px", zIndex: 1000 }}
                  >
                    {/* User Info */}
                    <div className="d-flex align-items-center mb-3 ms-2">
                      <FontAwesomeIcon icon={faUserCircle} />
                      <span className="ms-2">{userDetail.firstName}</span>
                    </div>

                    {/* Theme Toggle */}
                    <div
                      className="d-flex align-items-center mb-3 ms-2"
                      onClick={toggleTheme}
                      role="button"
                      style={{ cursor: "pointer" }}
                    >
                      <FontAwesomeIcon
                        icon={theme === "dark" ? faSun : faMoon}
                        className={
                          theme === "dark"
                            ? "text-warning"
                            : "text-body-emphasis"
                        }
                      />
                      <span className="ms-2">
                        {theme === "dark" ? "Light Mode" : "Dark Mode"}
                      </span>
                    </div>

                    <div className="dropdown-divider border-secondary"></div>

                    {/* Logout */}
                    <button
                      className="dropdown-item text-white bg-transparent glare rounded-3 ps-2"
                      onClick={logout}
                    >
                      <FontAwesomeIcon icon={faSignOut} /> Logout
                    </button>
                  </div>
                )}
              </li>
            </ul>
          </div>
        </div>

        {/* Mobile Offcanvas Menu */}
        <div
          className="offcanvas offcanvas-start text-bg-dark d-lg-none"
          tabIndex="-1"
          id="mobileMenu"
          aria-labelledby="mobileMenuLabel"
        >
          <div className="offcanvas-header">
            <h5 className="offcanvas-title text-white" id="mobileMenuLabel">
              Menu
            </h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              data-bs-dismiss="offcanvas"
              aria-label="Close"
            ></button>
          </div>

          <div className="offcanvas-body d-flex flex-column gap-3">
            {Object.entries(sections).map(([key, label]) => (
              <span key={key} data-bs-dismiss="offcanvas">
                <Link
                  className="text-white text-decoration-none fw-medium"
                  to={`/${key}`}
                >
                  {label}
                </Link>
              </span>
            ))}

            <hr className="border-secondary" />

            <button className="btn btn-outline-light" onClick={logout}>
              Logout
            </button>
          </div>
        </div>
      </nav>

      {/* ✅ Full Width Notice Bar */}
      <div
        className="text-center py-2"
        style={{
          background: "#2a1f00",
          color: "#ffc107",
          fontSize: "0.9rem",
          width: "100%",
        }}
      >
       <span className="fw-bold"> Please book profit in all stocks for WealthX Portfolio and re-invest the funds in Alpha India Portfolio.</span><br/> For further assistance call our team on +91 9898916110
      </div>
    </>
  );
}

export default Header;
